import { useState } from "react";

interface Props {
  images: string[];
  productName: string;
  badge?: string;
  isNew?: boolean;
  saveDollars?: number | null;
  inStock: boolean;
}

export default function ProductGallery({ images, productName, badge, isNew, saveDollars, inStock }: Props) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      {/* Main image */}
      <div
        className="relative w-full bg-gray-50 rounded-xl overflow-hidden cursor-zoom-in"
        style={{ aspectRatio: "1 / 1" }}
        onClick={() => setZoomed(true)}
      >
        <img
          src={images[activeIdx]}
          alt={`${productName} — view ${activeIdx + 1}`}
          className="w-full h-full object-contain p-6 transition-opacity duration-200"
        />
        {/* Overlay badges */}
        {saveDollars && inStock && (
          <span className="absolute top-4 left-4 bg-red-500 text-white text-xs font-extrabold px-3 py-1.5 rounded-md">
            Save ${saveDollars.toLocaleString()}
          </span>
        )}
        {!inStock && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="bg-gray-700 text-white text-sm font-bold px-4 py-2 rounded-lg">Sold Out</span>
          </div>
        )}
        {badge && inStock && !saveDollars && (
          <span className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-md">
            {badge}
          </span>
        )}
        {isNew && inStock && (
          <span className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-md">
            New Arrival
          </span>
        )}
        {/* Zoom hint */}
        <div className="absolute bottom-3 right-3 bg-white/80 rounded-full w-8 h-8 flex items-center justify-center text-gray-500">
          <i className="ri-zoom-in-line text-sm"></i>
        </div>
      </div>

      {/* Thumbnails */}
      <div className="flex gap-3">
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => setActiveIdx(i)}
            className={`relative w-20 h-20 rounded-lg overflow-hidden bg-gray-50 border-2 transition-all cursor-pointer shrink-0 ${
              activeIdx === i ? "border-amber-500" : "border-gray-100 hover:border-gray-300"
            }`}
          >
            <img src={img} alt={`${productName} thumbnail ${i + 1}`} className="w-full h-full object-contain p-2" />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {zoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setZoomed(false)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-white bg-white/10 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
            onClick={() => setZoomed(false)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <img
            src={images[activeIdx]}
            alt={productName}
            className="max-w-3xl w-full max-h-[80vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {/* Prev / Next in lightbox */}
          {images.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-colors"
                onClick={(e) => { e.stopPropagation(); setActiveIdx((activeIdx - 1 + images.length) % images.length); }}
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-colors"
                onClick={(e) => { e.stopPropagation(); setActiveIdx((activeIdx + 1) % images.length); }}
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
